import { createContext, useCallback, useContext } from 'react'

import Loader from '@/components/ui/Loader/Loader'
import { useGetUserQuery } from '@/contexts/user/operations.generated'
import { useAuthenticationProvider } from '@/providers/authentication/Authentication.context'

import type { GetUserQuery } from '@/contexts/user/operations.generated'
import type { ReactNode } from 'react'

type UserContext = {
  user: Extract<GetUserQuery['viewer'], { __typename?: 'User' }>
  fetchUser: () => Promise<void>
  isLoading: boolean
  isConnected: boolean
}

const UserContext = createContext<UserContext>({
  user: {} as UserContext['user'],
  fetchUser: () => Promise.resolve(),
  isLoading: false,
  isConnected: false,
})

type UserProviderProps = {
  children: ReactNode
}

/**
 * We consider that user is defined when the user is authenticated and the user data is fetched
 * Otherwise, it will fallback to Login path in Router.tsx
 */
export const UserProvider = ({ children }: UserProviderProps) => {
  const { isAuthenticated, isLoading } = useAuthenticationProvider()

  const { data, loading, refetch } = useGetUserQuery({
    skip: !isAuthenticated,
  })

  const userProviderIsLoading = isLoading || loading
  const isConnected = Boolean(data?.viewer)
  const user = data?.viewer

  const fetchUser = useCallback(async () => {
    refetch()
  }, [])

  if (userProviderIsLoading && !user) {
    return <Loader />
  }

  const value: UserContext = { user: user!, fetchUser, isLoading: userProviderIsLoading, isConnected }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export const useUser = () => useContext(UserContext)
